/**
 * PropertyLeverPanel — Phase C per-property what-if lever.
 *
 * One panel per active real-estate holding on the Scenarios page. The user
 * toggles "include in scenario", picks Full Sale vs Downsize, and adjusts
 * the price. The panel is data-driven from `/api/property/plans` so any
 * customer with N properties gets N panels — no hardcoded "Farm Avenue".
 */
import { Home, AlertTriangle } from 'lucide-react'
import { clsx } from 'clsx'

import type { PropertySalePlanEnriched } from '@/lib/types'

export type LeverMode = 'sell' | 'downsize'

export interface PropertyLeverState {
  enabled: boolean
  mode: LeverMode
  sale_price_gbp: number
  downsize_to_gbp: number
}

export function defaultLeverState(plan: PropertySalePlanEnriched): PropertyLeverState {
  const gross = plan.gross_value_gbp ?? plan.net_proceeds_gbp ?? 500_000
  return {
    enabled: false,
    mode: 'sell',
    sale_price_gbp: gross,
    downsize_to_gbp: Math.round(gross * 0.6),
  }
}

interface PropertyLeverPanelProps {
  plan: PropertySalePlanEnriched
  state: PropertyLeverState
  onChange: (next: PropertyLeverState) => void
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

export default function PropertyLeverPanel({
  plan,
  state,
  onChange,
}: PropertyLeverPanelProps) {
  const gross = plan.gross_value_gbp ?? 0
  const mortgage = plan.outstanding_mortgage_gbp ?? 0
  const equity = plan.equity_gbp ?? Math.max(0, gross - mortgage)
  const saleCostsPct = plan.sale_costs_pct ?? 2.5

  const set = (patch: Partial<PropertyLeverState>) => onChange({ ...state, ...patch })

  // Live preview of the freed equity / proceeds based on current mode + price.
  let previewProceeds = 0
  if (state.mode === 'sell') {
    previewProceeds = Math.max(
      0,
      state.sale_price_gbp * (1 - saleCostsPct / 100) - mortgage,
    )
  } else {
    const freedGross = Math.max(0, gross - state.downsize_to_gbp)
    const costs = gross * (saleCostsPct / 100)
    previewProceeds = Math.max(0, freedGross - costs)
  }

  return (
    <div
      className={clsx(
        'card p-3 space-y-2',
        state.enabled ? 'border-blue/40 bg-blue/5' : '',
      )}
    >
      <div className="flex items-start gap-2">
        <Home size={14} className="mt-0.5 text-text-muted shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-text-primary truncate">
              {plan.label || plan.holding_id}
            </span>
            <button
              type="button"
              onClick={() => set({ enabled: !state.enabled })}
              className={clsx(
                'shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border transition-colors',
                state.enabled
                  ? 'bg-blue/20 text-blue-light border-blue/40'
                  : 'bg-surface-card text-text-muted border-border hover:text-text-primary',
              )}
              aria-pressed={state.enabled}
            >
              {state.enabled ? 'Active' : 'Off'}
            </button>
          </div>
          <p className="text-[11px] text-text-muted leading-tight">
            Gross {fmt(gross)} · Mortgage {fmt(mortgage)} · Equity {fmt(equity)}
          </p>
          {plan.urgent_note && (
            <p className="text-[11px] mt-1 text-amber flex items-start gap-1">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden />
              {plan.urgent_note}
            </p>
          )}
        </div>
      </div>

      {state.enabled && (
        <div className="space-y-2 pt-1 border-t border-border/50">
          {/* Mode toggle: full sale vs downsize */}
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => set({ mode: 'sell' })}
              className={clsx(
                'flex-1 text-[10px] font-medium px-2 py-1 rounded border transition-colors',
                state.mode === 'sell'
                  ? 'bg-blue/15 border-blue/40 text-blue-light'
                  : 'bg-surface-card border-border text-text-muted hover:text-text-primary',
              )}
            >
              Full sale
            </button>
            <button
              type="button"
              onClick={() => set({ mode: 'downsize' })}
              className={clsx(
                'flex-1 text-[10px] font-medium px-2 py-1 rounded border transition-colors',
                state.mode === 'downsize'
                  ? 'bg-blue/15 border-blue/40 text-blue-light'
                  : 'bg-surface-card border-border text-text-muted hover:text-text-primary',
              )}
            >
              Downsize
            </button>
          </div>

          {state.mode === 'sell' ? (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-text-muted">
                Sale price
              </span>
              <input
                type="number"
                value={state.sale_price_gbp}
                min={0}
                step={5_000}
                onChange={(e) => set({ sale_price_gbp: parseFloat(e.target.value) || 0 })}
                className="w-full mt-0.5 px-2 py-1 text-sm bg-surface-DEFAULT border border-border rounded font-mono"
              />
            </label>
          ) : (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-text-muted">
                Replacement-property value
              </span>
              <input
                type="number"
                value={state.downsize_to_gbp}
                min={0}
                max={Math.max(0, gross)}
                step={5_000}
                onChange={(e) => set({ downsize_to_gbp: parseFloat(e.target.value) || 0 })}
                className="w-full mt-0.5 px-2 py-1 text-sm bg-surface-DEFAULT border border-border rounded font-mono"
              />
            </label>
          )}

          <p className="text-[11px] text-text-secondary leading-tight">
            ≈ {fmt(previewProceeds)} freed to liquid pot ({saleCostsPct.toFixed(1)}% costs
            {state.mode === 'sell' ? `, ${fmt(mortgage)} mortgage cleared` : ', mortgage rolls over'})
          </p>
        </div>
      )}
    </div>
  )
}
